import React, { forwardRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  StyledInput,
  InputWrapper,
  InputRow,
  InputLabel,
  ErrorMessage,
} from './styled';
import styled, { css } from 'styled-components';
import { media } from 'responsive/media';

const PasswordWrapper = styled.div`
  position: relative;
  display: flex;
  width: ${(props) => (props.$widthInput ? `${props.$widthInput}px` : '100%')};
  max-height: ${(props) =>
    props.$heightInput ? `${props.$heightInput}px` : '40px'};

  ${media.xxlDown(css`
    min-height: 36px !important;
    width: ${({ widthInput, $isPopup }) =>
      $isPopup
        ? '100%'
        : parseFloat(widthInput)
          ? `${parseFloat(widthInput) * 0.8}px`
          : '310px'} !important;
  `)}
  ${media.xlDown(css`
    height: 32px !important;
    width: ${({ widthInput, $isPopup }) =>
      $isPopup
        ? '100%'
        : parseFloat(widthInput)
          ? `${parseFloat(widthInput) * 0.8}px`
          : '310px'} !important;
  `)}
`;

const ToggleButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #999999;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;

  &:hover {
    color: #ed7836;
  }

  &:disabled {
    cursor: not-allowed;
    color: #ccc;
  }
`;

const StyledPasswordInput = styled(StyledInput)`
  padding-right: 40px;
  width: ${(props) => (props.widthInput ? `${props.widthInput}px` : '400px')};
  height: ${(props) =>
    props.$heightInput ? `${props.$heightInput}px` : '40px'};

  @media (max-width: 1536px) {
    height: 36px !important;
    width: ${({ widthInput, $isPopup }) =>
      $isPopup
        ? '100%'
        : parseFloat(widthInput)
          ? `${parseFloat(widthInput) * 0.85}px`
          : '310px'} !important;
  }
  @media (max-width: 1280px) {
    height: 34px !important;
    width: ${({ widthInput, $isPopup }) =>
      $isPopup
        ? '100%'
        : parseFloat(widthInput)
          ? `${parseFloat(widthInput) * 0.7}px`
          : '310px'} !important;
  }
`;

const EyeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <path d="M8 2C4.5 2 1.73 4.61 1 8c.73 3.39 3.5 6 7 6s6.27-2.61 7-6c-.73-3.39-3.5-6-7-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
  </svg>
);

const EyeOffIcon = () => (
  <div tabIndex={-1}>
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
      <path d="M8 2C4.5 2 1.73 4.61 1 8c.73 3.39 3.5 6 7 6s6.27-2.61 7-6c-.73-3.39-3.5-6-7-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
      <path d="M2 2l12 12" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  </div>
);

const PasswordInput = forwardRef(
  (
    {
      label,
      required,
      rules = [],
      normalize,
      placeholder,
      widthLabel,
      widthInput,
      heightInput,
      value,
      errors,
      onChange,
      onFocus,
      disabled,
      name,
      id,
      className,
      isPopup = true,
      style,
      paddingMessage,
      onValidationChange,
      // validateTrigger = ['onChange'],
      visibilityToggle = true,
      ...rest
    },
    ref
  ) => {
    const inputId = id || name;
    const [visible, setVisible] = useState(false);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const allRules = [...rules];

    // Check if there's an error for this field in the errors prop
    const externalError = errors && errors.find((err) => err.label === name);
    const hasExternalError = !!externalError;
    const externalErrorMessage = externalError ? externalError.message : '';

    const validateValue = async (val, trigger = 'onChange') => {
      const normalizedValue = normalize ? normalize(val) : val;

      for (const rule of allRules) {
        // Skip rule if validateTrigger doesn't match current trigger
        if (rule.validateTrigger && !rule.validateTrigger.includes(trigger)) {
          continue;
        }

        // Required validation
        if (
          rule.required &&
          (!normalizedValue || normalizedValue.toString().trim() === '')
        ) {
          setError(true);
          setErrorMessage(
            rule.message || `${label || 'This field'} is required`
          );
          onValidationChange && onValidationChange(false, rule.message);
          return false;
        }

        // Custom validator
        if (rule.validator && normalizedValue) {
          try {
            await rule.validator(null, normalizedValue);
          } catch (errorMsg) {
            setError(true);
            setErrorMessage(errorMsg);
            onValidationChange && onValidationChange(false, errorMsg);
            return false;
          }
        }

        // Pattern validation
        if (
          rule.pattern &&
          normalizedValue &&
          !rule.pattern.test(normalizedValue)
        ) {
          setError(true);
          setErrorMessage(rule.message || 'Invalid format');
          onValidationChange && onValidationChange(false, rule.message);
          return false;
        }

        // Min/Max length validation
        if (rule.min && normalizedValue && normalizedValue.length < rule.min) {
          setError(true);
          setErrorMessage(
            rule.message || `Minimum ${rule.min} characters required`
          );
          onValidationChange && onValidationChange(false, rule.message);
          return false;
        }

        if (rule.max && normalizedValue && normalizedValue.length > rule.max) {
          setError(true);
          setErrorMessage(
            rule.message || `Maximum ${rule.max} characters allowed`
          );
          onValidationChange && onValidationChange(false, rule.message);
          return false;
        }
      }

      // No errors
      setError(false);
      setErrorMessage('');
      onValidationChange && onValidationChange(true, '');
      return true;
    };

    // Handle change
    const handleChange = (e) => {
      const inputValue = e.target.value.trim();
      onChange && onChange({ label: name, value: inputValue });
      validateValue(inputValue, 'onChange');
    };

    // Handle focus
    const handleFocus = (e) => {
      if (onFocus) {
        onFocus(e);
      }
    };

    // Toggle visibility
    const toggleVisibility = () => {
      setVisible(!visible);
    };

    return (
      <InputWrapper className={className} style={style}>
        <InputRow>
          {label && (
            <InputLabel
              htmlFor={inputId}
              $widthLabel={widthLabel}
              $isPopup={isPopup}
            >
              {label}
              {required && <span className="required">*</span>}
            </InputLabel>
          )}
          <PasswordWrapper
            $widthInput={widthInput}
            $heightInput={heightInput}
            $isPopup={isPopup}
          >
            <StyledPasswordInput
              ref={ref}
              id={inputId}
              $widthInput={widthInput}
              $heightInput={heightInput}
              type={visible ? 'text' : 'password'}
              placeholder={placeholder}
              $isPopup={isPopup}
              value={value}
              onChange={handleChange}
              onFocus={handleFocus}
              disabled={disabled}
              className={error || hasExternalError ? 'error' : ''}
              {...rest}
              name={name}
            />
            {visibilityToggle && (
              <ToggleButton
                type="button"
                onClick={toggleVisibility}
                disabled={disabled}
              >
                {!visible ? <EyeOffIcon /> : <EyeIcon />}
              </ToggleButton>
            )}
          </PasswordWrapper>
        </InputRow>
        <ErrorMessage
          $show={(error && errorMessage) || hasExternalError || undefined}
          $paddingMessage={paddingMessage}
        >
          {hasExternalError ? externalErrorMessage : errorMessage}
        </ErrorMessage>
      </InputWrapper>
    );
  }
);

PasswordInput.displayName = 'PasswordInput';

PasswordInput.propTypes = {
  label: PropTypes.string,
  required: PropTypes.bool,
  rules: PropTypes.arrayOf(
    PropTypes.shape({
      required: PropTypes.bool,
      message: PropTypes.string,
      validator: PropTypes.func,
      pattern: PropTypes.instanceOf(RegExp),
      min: PropTypes.number,
      max: PropTypes.number,
    })
  ),
  normalize: PropTypes.func,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  errors: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      message: PropTypes.string,
    })
  ),
  onChange: PropTypes.func,
  widthLabel: PropTypes.number,
  widthInput: PropTypes.number,
  heightInput: PropTypes.number,
  onFocus: PropTypes.func,
  disabled: PropTypes.bool,
  name: PropTypes.string,
  id: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  paddingMessage: PropTypes.number,
  onValidationChange: PropTypes.func,
  validateTrigger: PropTypes.arrayOf(PropTypes.oneOf(['onChange'])),
  visibilityToggle: PropTypes.bool,
};

export default PasswordInput;
